import { SPEC_FILE_NAME } from "@/constants";
import { useActiveFileName } from "@/lib/hooks/useActiveFileName";
import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { useActiveSnapshotId } from "@/lib/hooks/useActiveSnapshot";
import { cn } from "@/lib/utils";
import { useFilesystemStore } from "@/state/stores";
import { Link } from "@tanstack/react-router";
import { File } from "lucide-react";
import { useShallow } from "zustand/react/shallow";

export function FileList() {
  const projectId = useActiveProjectId();
  const activeSnapshotId = useActiveSnapshotId();
  const currentFileName = useActiveFileName();

  const allFiles = useFilesystemStore(
    useShallow((state) => {
      // Get files from the active snapshot, fall back to staged files
      const files =
        state.filesBySnapshotId[activeSnapshotId] || state.stagedFiles;

      // Sort by filename, extract just the filenames, and filter out the spec file
      return files
        .sort((a, b) => a.path.localeCompare(b.path))
        .map((file) => file.path)
        .filter((file) => file !== SPEC_FILE_NAME);
    }),
  );

  if (!projectId || allFiles.length === 0) {
    return null;
  }

  return (
    <ul className="flex flex-col gap-2 bg-muted p-2 h-full overflow-y-auto">
      {allFiles.map((filename) => (
        <li key={filename}>
          <Link
            to="/codegen/$projectId/filesystem/$snapshotId"
            params={{
              projectId,
              snapshotId: activeSnapshotId,
            }}
            search={{
              tab: filename === SPEC_FILE_NAME ? "spec" : "code",
              file: filename,
            }}
            className={cn(
              "font-mono text-xs grid grid-cols-[auto_1fr] gap-2 items-center px-2 py-1 hover:bg-muted-foreground/20 rounded-sm truncate",
              filename === currentFileName &&
                "bg-muted-foreground/20 hover:bg-muted-foreground/30",
            )}
          >
            <File className="w-4 h-4" />
            {filename}
          </Link>
        </li>
      ))}
    </ul>
  );
}
